import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Download,
  HelpCircle,
  RefreshCw,
} from "lucide-react";
import { Link } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  CampaignEngagement,
  CampaignEngagementContact,
} from "@shared-core/data/marketing-types";
import {
  getApiBaseUrl,
  joinApiUrl,
  getAuthToken,
} from "@shared-core/data/api/client";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { formatDateTimeOrDash } from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { StatCard } from "../../patterns/StatCard";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../primitives/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../primitives/tooltip";

type State =
  | { status: "loading" }
  | { status: "error" }
  | { status: "not_synced" }
  | { status: "ready"; engagement: CampaignEngagement };

// Derived from the same precedence as the Status badge below: an
// unsubscribed or bounced contact is more noteworthy than a plain delivery.
type ContactStatus = "unsubscribed" | "bounced" | "delivered" | "none";

function statusOf(c: CampaignEngagementContact): ContactStatus {
  if (c.unsubscribed) return "unsubscribed";
  if (c.bounced) return "bounced";
  if (c.delivered) return "delivered";
  return "none";
}

// Mailchimp's delivery-status report and its open/click report are pulled
// separately (see ingest_campaign_engagement.py) and occasionally disagree —
// a contact can be reported bounced yet still show opens/clicks from before
// that reclassification. Flag it rather than silently showing both as if
// compatible, so a rep doesn't read a bounced contact's opens as a live signal.
function hasConflictingSignals(c: CampaignEngagementContact): boolean {
  return c.bounced && (c.opened_count > 0 || c.clicked_count > 0);
}

const STATUS_RANK: Record<ContactStatus, number> = {
  none: 0,
  delivered: 1,
  bounced: 2,
  unsubscribed: 3,
};

type SortKey =
  | "delivered"
  | "opened_count"
  | "clicked_count"
  | "status"
  | "sales_rep_name"
  | "company_name";
type SortState = { key: SortKey; dir: "asc" | "desc" };

function compareContacts(
  a: CampaignEngagementContact,
  b: CampaignEngagementContact,
  sort: SortState | null,
): number {
  if (!sort) return a.email.localeCompare(b.email);
  const dir = sort.dir === "asc" ? 1 : -1;
  switch (sort.key) {
    case "delivered":
      return (Number(a.delivered) - Number(b.delivered)) * dir;
    case "opened_count":
      return (a.opened_count - b.opened_count) * dir;
    case "clicked_count":
      return (a.clicked_count - b.clicked_count) * dir;
    case "status":
      return (STATUS_RANK[statusOf(a)] - STATUS_RANK[statusOf(b)]) * dir;
    case "sales_rep_name":
      // Unassigned sorts last regardless of direction, so toggling sort
      // order shuffles named reps without burying them under a pile of
      // unassigned contacts at one end.
      if (a.sales_rep_name === null && b.sales_rep_name === null) return 0;
      if (a.sales_rep_name === null) return 1;
      if (b.sales_rep_name === null) return -1;
      return a.sales_rep_name.localeCompare(b.sales_rep_name) * dir;
    case "company_name":
      // Unresolved company sorts last regardless of direction, same
      // rationale as sales_rep_name above.
      if (a.company_name === null && b.company_name === null) return 0;
      if (a.company_name === null) return 1;
      if (b.company_name === null) return -1;
      return a.company_name.localeCompare(b.company_name) * dir;
  }
}

function SortableHeader({
  label,
  sortKey,
  sort,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  sort: SortState | null;
  onSort: (key: SortKey) => void;
}) {
  const active = sort?.key === sortKey;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className="inline-flex items-center gap-1 hover:text-foreground"
    >
      {label}
      <Icon className="size-3.5 text-muted-foreground" />
    </button>
  );
}

function formatPct(fraction: number): string {
  return `${(fraction * 100).toFixed(1)}%`;
}

// Delivered/Bounced mirror Mailchimp's own aggregate report numbers
// (see _pull_summary in ingest_campaign_engagement.py) rather than being
// recomputed from our per-contact event rows below — the two Mailchimp
// endpoints occasionally disagree, so the tile carries a caveat.
function StatLabelWithCaveat({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      {label}
      <Tooltip>
        <TooltipTrigger asChild>
          <HelpCircle className="size-3.5 text-muted-foreground cursor-help" />
        </TooltipTrigger>
        <TooltipContent className="max-w-64">
          This figure comes directly from Mailchimp&apos;s campaign report and
          may differ slightly from the per-contact list below — Mailchimp
          computes them independently.
        </TooltipContent>
      </Tooltip>
    </span>
  );
}

// The Engagement tab on a campaign's workspace: composed Mailchimp open/
// click/bounce/unsubscribe rates plus the per-contact list a sales rep scans
// to decide who's worth calling. Self-contained fetch, same pattern as the
// Insights tabs — mirrors GET /views/campaigns/{id}/engagement, which
// returns null (not 404) for "not sent yet" / "sent but never synced".
export function CampaignEngagementTab({
  campaignId,
  stepId,
}: {
  campaignId: string;
  stepId: string;
}) {
  const adapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  const [state, setState] = useState<State>({ status: "loading" });
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortState | null>(null);
  // Hooks must run unconditionally on every render — computed here, above
  // the loading/error/not_synced early returns below, even though it's only
  // meaningful once state.status === "ready".
  const sortedContacts = useMemo(() => {
    const contacts = state.status === "ready" ? state.engagement.contacts : [];
    return [...contacts].sort((a, b) => compareContacts(a, b, sort));
  }, [state, sort]);

  function handleSort(key: SortKey) {
    setSort((prev) =>
      prev?.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" },
    );
  }

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const result = await adapter.getCampaignEngagement(stepId);
      setState(
        result
          ? { status: "ready", engagement: result }
          : { status: "not_synced" },
      );
    } catch (e) {
      console.error("marketing.campaign.engagement.load.failed", e);
      setState({ status: "error" });
    }
  }, [adapter, stepId]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleRefresh() {
    setRefreshing(true);
    setRefreshError(null);
    try {
      const result = await adapter.refreshCampaignEngagement(
        campaignId,
        stepId,
      );
      setState({ status: "ready", engagement: result });
    } catch (e) {
      console.error("marketing.campaign.engagement.refresh.failed", e);
      setRefreshError("Could not pull from Mailchimp. Please try again.");
    } finally {
      setRefreshing(false);
    }
  }

  async function handleExportTopEngagersCsv() {
    setExporting(true);
    setExportError(null);
    try {
      const url = joinApiUrl(
        getApiBaseUrl(),
        `views/campaigns/${stepId}/engagement/top-engagers.csv`,
      );
      const token = await getAuthToken();
      const resp = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`Export failed: ${resp.status}`);
      const csv = await resp.text();
      capabilities.downloadTextFile(
        `${stepId}-top-engagers.csv`,
        csv,
        "text/csv",
      );
    } catch (e) {
      console.error(
        "marketing.campaign.engagement.export_top_engagers.failed",
        e,
      );
      setExportError("Could not export top engagers. Please try again.");
    } finally {
      setExporting(false);
    }
  }

  if (state.status === "loading") {
    return (
      <div className="text-sm text-muted-foreground">Loading engagement…</div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="text-sm p-8 text-center border border-border rounded-lg text-red-600">
        Couldn't load engagement. Please try again.
      </div>
    );
  }

  if (state.status === "not_synced") {
    return (
      <div className="p-8 text-center border border-dashed border-border rounded-lg space-y-3">
        <p className="text-sm text-muted-foreground">
          No Mailchimp engagement yet — this campaign hasn't synced from
          Mailchimp yet.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          <RefreshCw className={`size-4 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Pulling…" : "Pull engagement from Mailchimp"}
        </Button>
        {refreshError && <p className="text-xs text-red-600">{refreshError}</p>}
      </div>
    );
  }

  const { summary } = state.engagement;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          Last synced {formatDateTimeOrDash(summary.last_synced_at)}
        </p>
        <div className="flex flex-col items-end gap-1">
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportTopEngagersCsv}
              disabled={exporting}
              title="Download the top 100 engaged contacts per sales rep, for re-targeting via a Mailchimp upload — useful when there are too many tickets to call everyone individually."
            >
              <Download className="size-4" />
              {exporting ? "Exporting…" : "Export top engagers (CSV)"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw
                className={`size-4 ${refreshing ? "animate-spin" : ""}`}
              />
              {refreshing ? "Pulling…" : "Pull engagement from Mailchimp"}
            </Button>
          </div>
          {refreshError && (
            <p className="text-xs text-red-600">{refreshError}</p>
          )}
          {exportError && <p className="text-xs text-red-600">{exportError}</p>}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard
          label={<StatLabelWithCaveat label="Delivered" />}
          value={
            <>
              {formatPct(summary.delivered_rate)}
              <div className="text-sm text-muted-foreground font-normal">
                {summary.delivered_count} of {summary.emails_sent}
              </div>
            </>
          }
        />
        <StatCard
          label="Opened"
          value={
            <>
              {formatPct(summary.open_rate)}
              <div className="text-sm text-muted-foreground font-normal">
                {summary.unique_opens_count} unique
              </div>
            </>
          }
        />
        <StatCard
          label="Clicked"
          value={
            <>
              {formatPct(summary.click_rate)}
              <div className="text-sm text-muted-foreground font-normal">
                {summary.unique_clicks_count} unique
              </div>
            </>
          }
        />
        <StatCard
          label={<StatLabelWithCaveat label="Bounced" />}
          value={
            <>
              {formatPct(summary.bounce_rate)}
              <div className="text-sm text-muted-foreground font-normal">
                {summary.bounces_count} contacts
              </div>
            </>
          }
        />
        <StatCard
          label="Unsubscribed"
          value={
            <>
              {formatPct(summary.unsubscribe_rate)}
              <div className="text-sm text-muted-foreground font-normal">
                {summary.unsubscribes_count} contacts
              </div>
            </>
          }
        />
      </div>
      {/* Mailchimp's "unique" is per email: adding a tranche's and its
          reminder's figures double counts anyone who clicked both — which
          is exactly how the head of marketing's campaign totals went wrong.
          Point to the de-duplicated campaign-wide figure instead. */}
      <p className="text-xs text-muted-foreground -mt-3">
        Unique opens and clicks are Mailchimp's figures for this email only —
        someone who clicked this email and its reminder counts once in each. For
        unique clickers across the whole campaign, see{" "}
        <Link
          to={paths.marketingResults()}
          className="text-primary hover:underline"
        >
          Results
        </Link>
        .
      </p>

      <div>
        <h3 className="text-sm font-medium text-foreground mb-2">
          Who engaged — click a column to sort
        </h3>
        <div className="rounded-lg border border-border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="max-w-[220px]">Contact</TableHead>
                <TableHead>
                  <SortableHeader
                    label="Company"
                    sortKey="company_name"
                    sort={sort}
                    onSort={handleSort}
                  />
                </TableHead>
                <TableHead>
                  <SortableHeader
                    label="Delivered"
                    sortKey="delivered"
                    sort={sort}
                    onSort={handleSort}
                  />
                </TableHead>
                <TableHead>
                  <SortableHeader
                    label="Opens"
                    sortKey="opened_count"
                    sort={sort}
                    onSort={handleSort}
                  />
                </TableHead>
                <TableHead>
                  <SortableHeader
                    label="Clicks"
                    sortKey="clicked_count"
                    sort={sort}
                    onSort={handleSort}
                  />
                </TableHead>
                <TableHead>
                  <SortableHeader
                    label="Status"
                    sortKey="status"
                    sort={sort}
                    onSort={handleSort}
                  />
                </TableHead>
                <TableHead>
                  <SortableHeader
                    label="Sales rep"
                    sortKey="sales_rep_name"
                    sort={sort}
                    onSort={handleSort}
                  />
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedContacts.map((c) => (
                <TableRow key={c.email}>
                  <TableCell className="max-w-[220px]">
                    <div className="font-medium text-foreground break-words">
                      {c.email}
                    </div>
                    {c.phone && (
                      <div className="text-xs text-muted-foreground">
                        {c.phone}
                      </div>
                    )}
                    {c.person_id === null && (
                      <div className="text-xs text-muted-foreground">
                        Not matched to a contact in One
                      </div>
                    )}
                    {c.clicked_links.length > 0 && (
                      <div className="text-xs text-muted-foreground mt-0.5 break-all">
                        Clicked: {c.clicked_links.join(", ")}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {c.company_name ?? (
                      <span className="italic text-muted-foreground/50">—</span>
                    )}
                  </TableCell>
                  <TableCell>{c.delivered ? "Yes" : "No"}</TableCell>
                  <TableCell>{c.opened_count}</TableCell>
                  <TableCell>{c.clicked_count}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      {c.unsubscribed && (
                        <Badge variant="error">Unsubscribed</Badge>
                      )}
                      {!c.unsubscribed && c.bounced && (
                        <Badge variant="warning">Bounced</Badge>
                      )}
                      {!c.unsubscribed && !c.bounced && c.delivered && (
                        <Badge variant="success">Delivered</Badge>
                      )}
                      {hasConflictingSignals(c) && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <AlertTriangle
                              aria-label="Conflicting engagement signals"
                              className="size-3.5 text-amber-600 cursor-help"
                            />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-64">
                            Mailchimp reported this contact as bounced but also
                            recorded opens/clicks for them — its delivery-status
                            and open/click reports are pulled separately and can
                            disagree. Treat the open/click activity here with
                            caution.
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {c.sales_rep_name ?? (
                      <span className="text-xs text-muted-foreground">
                        Unassigned
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
